/**
 * Seeded random number generator (mulberry32). The whole simulation draws from
 * one of these, so a given seed plus difficulty always replays identically. That
 * makes bugs reproducible, and it lets us share scores against a named seed
 * later.
 */
export class Rng {
  private s: number;

  constructor(seed: number) {
    this.s = seed >>> 0;
  }

  /** Uniform float in [0, 1). */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform float in [a, b). */
  range(a: number, b: number): number {
    return a + this.next() * (b - a);
  }

  /** Uniform integer in [a, b]. */
  int(a: number, b: number): number {
    return a + Math.floor(this.next() * (b - a + 1));
  }

  /** True with probability p. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T {
    return items[Math.floor(this.next() * items.length)];
  }

  /** A fresh generator made from this one, so that each part of the game draws its own numbers. */
  fork(): Rng {
    return new Rng(Math.floor(this.next() * 0xffffffff));
  }
}

/** Cheap value noise in one dimension: a smooth random wobble that the same seed always repeats. */
export function noise1(rng: Rng, count: number, octaves = 3): Float32Array {
  const out = new Float32Array(count);
  let amp = 1;
  let total = 0;
  for (let o = 0; o < octaves; o++) {
    const step = Math.max(2, Math.round(count / (2 << o)));
    const pts = Math.ceil(count / step) + 2;
    const ctrl = new Float32Array(pts);
    for (let i = 0; i < pts; i++) ctrl[i] = rng.range(-1, 1);
    for (let i = 0; i < count; i++) {
      const f = i / step;
      const i0 = Math.floor(f);
      const t = f - i0;
      const s = t * t * (3 - 2 * t);
      out[i] += (ctrl[i0] * (1 - s) + ctrl[i0 + 1] * s) * amp;
    }
    total += amp;
    amp *= 0.5;
  }
  for (let i = 0; i < count; i++) out[i] /= total;
  return out;
}
